import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { AdminService } from '@app/services/admin.service';
import { CussService } from '@app/services/cuss.service';
import packageInfo from '../../../package.json';
import { v4 as uuidv4 } from 'uuid';
import { NewRelicService } from '@app/services/micro/new-relic.service';
import { NewRelicParmsInt } from '@app/interfaces/ga4.interface';
import { environment } from '@env/environment';
import { DECIMALS_SERVICES, DECIMALS_HEADER } from '@app/utils/decimals-services.config';
import {
  ENCRYPTED_REDACTION,
  ENCRYPTED_SERVICE_SENSITIVE_HEADERS,
  findEncryptedService,
} from '@app/config/encrypted-services.config';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  urlCheckin = 'checkIn';
  // flightstatus (IROPS) en el BFF: se le agregan los headers de addHeadersIropsMicro.
  baseIropsMicro = '/kiosk/checkin/flightstatus';
  endPointName = '';
  urlBaseMicros = [
    environment.urlApiMicro,
    environment.urlApiCheckin,
    environment.urlApiMyTrips,
    environment.urlApiCheckinBFF,
    environment.urlApiMicroEligibility,
    environment.urlApiContactability,
    environment.urlApiContactabilityAuth,
    environment.urlApiSelfReaccom,
    environment.urlApiWeb,
    environment.urlAncillaries,
    environment.urlApiPayment,
  ];

  urlBaseMonolite = [environment.urlApi];

  urlsGetMonolite = ['carts', 'boarding-passes', 'issueBagTag'];

  endPointMicroservices = ['docheckin', 'getBoardingPass'];

  constructor(
    private _adminService: AdminService,
    private _cussService: CussService,
    private _newRelicService: NewRelicService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const { url } = req;
    let paramsHttp;
    this._newRelicService.kioskID = this._cussService.KioskID;
    if (url.includes(this.baseIropsMicro)) {
      paramsHttp = this.addHeadersIropsMicro(req);
      next.handle(paramsHttp);
    } else if (url.includes(this.urlCheckin) && this._adminService.admin) {
      if (this.isExcludedFromCheckIn(url)) {
        paramsHttp = req;
      } else {
        paramsHttp = this.addParametersCheckIn(req);
      }

      next.handle(paramsHttp);
    } else {
      // Error 403 CMS - Excluir URL del CMS del interceptor para evitar enviar kioskId=null
      if (url.includes(environment.cmsUrl)) {
        paramsHttp = req;
      } else if (url.includes('/exchange/reassociation')) {
        paramsHttp = req;
      } else if (this.isExcludedFromCheckIn(url)) {
        paramsHttp = req;
      } else {
        paramsHttp = this.addParameterIdKiosk(req);
      }

      next.handle(req.clone());
    }
    paramsHttp = this.addNumberOfDecimals(paramsHttp ?? req);
    return next.handle(paramsHttp).pipe(
      tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.sendMetricNewRelic(paramsHttp, event);
          }
        },
        error: (error) => {
          this.sendMetricNewRelic(paramsHttp, error);
        },
      })
    );
  }

  private isExcludedFromCheckIn(url: string): boolean {
    return this.urlBaseMicros.some((excludedUrl) => url.includes(excludedUrl));
  }

  addParametersCheckIn(req: HttpRequest<unknown>) {
    const { store, TSA, DOCV, Location, currency, Payment, iropReaccommed } =
      this._adminService.admin;

    const timeOfRequest = new Date().getTime().toString();
    return req.clone({
      params: req.params
        .set('timeOfRequest', timeOfRequest)
        .set('pos', 'kiosk')
        .set('store', store ?? 'MX')
        .set('overrideTsa', !TSA)
        .set('overrideDocv', DOCV)
        .set('kioskId', this._cussService.KioskID)
        .set('uiVersion', packageInfo.version)
        .set('location', Location)
        .set('currency', currency ?? 'MXN')
        .set('paymentEnabled', Payment)
        .set('uiVersion', '4.5.19-109')
        .set('languageUI', 'es_MX')
        .set('language', 'ES')
        .set('uuid', uuidv4())
        .set('reaccommed', iropReaccommed),
    });
  }

  addParameterIdKiosk(req: HttpRequest<unknown>) {
    const timeOfRequest = new Date().getTime().toString();
    return req.clone({
      params: req.params
        .set('timeOfRequest', timeOfRequest)
        .set('pos', 'kiosk')
        .set('kioskId', this._cussService.KioskID)
        .set('id', this._cussService.KioskID),
    });
  }

  private addNumberOfDecimals(req: HttpRequest<unknown>): HttpRequest<unknown> {
    if (!DECIMALS_SERVICES.some((s) => req.url.includes(s))) {
      return req;
    }
    const decimals = this._adminService.admin?.numberOfDecimalsBE;
    if (decimals == null) {
      return req;
    }
    return req.clone({ setHeaders: { [DECIMALS_HEADER]: String(decimals) } });
  }

  addHeadersIropsMicro(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const cartId = this._adminService.admin.cartId;

    return req.clone({
      setHeaders: {
        accept: 'application/json',
        flow: 'CHECKIN',
        'x-transaction-id': cartId || '',
        channel: 'KIOSK',
        'Content-type': 'application/json',
      },
    });
  }

  async sendMetricNewRelic(request: HttpRequest<unknown>, response: HttpResponse<unknown>) {
    let metricsNewRelic: NewRelicParmsInt;
    if (this.urlBaseMicros.find((url) => response.url?.includes(url))) {
      if (this.isEndPointMicroservices(request, response)) {
        await this.sendMetricNewRelic2(request, response);
      } else {
        metricsNewRelic = await this.getMetricsNewRelic(request, response);
        this._newRelicService.sendMetric(metricsNewRelic);
      }
    } else if (this.urlBaseMonolite.find((url) => response?.url?.includes(url))) {
      metricsNewRelic = await this.getMetricsNewRelic(request, response);
      this._newRelicService.sendMetricMonolite(metricsNewRelic);
    }
  }

  isEndPointMicroservices(request, response) {
    if (this.urlBaseMicros.find((url) => response?.url?.includes(url))) {
      let name = request?.url?.split('/')?.pop();
      name = this.urlsGetMonolite.find((endpoint) => request?.url?.includes(endpoint)) ?? name;
      name = name.includes('?') ? name.split('?')[0] : name;

      if (
        this.endPointMicroservices.find((endpoint) => endpoint === name) &&
        response.error === undefined
      ) {
        this.endPointName = name;

        return true;
      }
    }

    return false;
  }

  async getMetricsNewRelic(request, response) {
    const { method } = request;

    let paramsObject: Record<string, string>;
    if (method === 'GET' && request?.params?.keys().length > 0) {
      const { params } = request;
      paramsObject = {};
      for (const key of params.keys()) {
        paramsObject[key] = params.get(key) || '';
      }
    }

    const associatedRequest: any = {
      url: request?.url,
      method: request?.method,
      headers: request?.headers,
      body: request?.body,
      params: paramsObject,
    };
    const associatedResponse: any = {
      url: response?.url,
      status: response?.status,
      headers: response?.headers,
      body: response?.body || response?.error,
    };

    // Servicios cifrados: Seguridad no permite enviar a New Relic la data en claro.
    // Los errores (en claro desde el backend) se conservan para diagnóstico.
    // Los servicios con newRelicRedaction: false (docheckin, getBoardingPass, bagtag,
    // flightDetails, flightstatus, profile) siguen llegando en claro.
    const encryptedService = findEncryptedService(request?.method, request?.url);
    if (encryptedService && encryptedService.newRelicRedaction !== false) {
      this.redactEncryptedMetric(
        associatedRequest,
        associatedResponse,
        this.isError(response) === 'true'
      );
    }

    let name = associatedRequest?.url?.split('/')?.pop();
    name =
      this.urlsGetMonolite.find((endpoint) => associatedRequest?.url?.includes(endpoint)) ?? name;
    name = name.includes('?') ? name.split('?')[0] : name;
    if (name == '' && associatedRequest?.url?.includes(environment.urlApi)) {
      name = 'checkIn';
    }

    if (associatedRequest.url.includes('/exchanges/am-exchanges-api/pnr')) {
      name = 'pnrIrops';
    }
    const dataEndPoint = { associatedRequest, associatedResponse };
    const dataNewRelic: NewRelicParmsInt = {
      admin: this._adminService.getDataAdminInt(),
      enviroment: environment.production,
      microRequest: dataEndPoint.associatedRequest,
      microResponse: dataEndPoint.associatedResponse,
      url: dataEndPoint.associatedRequest.url,
      name,
      transactionId: uuidv4(),
      passengerId: '',
      isError: this.isError(response),
      route: '',
      bookedLegCollection: dataEndPoint.associatedResponse?.body?.bookedLegCollection,
    };

    if (name == 'pnr') {
      // Crear una copia del body sin bookedLegCollection para NewRelic
      const bodyWithoutBookedLeg = { ...dataEndPoint.associatedResponse?.body };
      delete bodyWithoutBookedLeg.bookedLegCollection;
      dataNewRelic.microResponse = {
        ...dataEndPoint.associatedResponse,
        body: bodyWithoutBookedLeg,
      };
    }
    return dataNewRelic;
  }

  /**
   * Sustituye bodies y params por ENCRYPTED_REDACTION y enmascara los headers
   * con datos del pasajero. Muta los objetos de métrica, nunca el request real.
   */
  private redactEncryptedMetric(
    associatedRequest: any,
    associatedResponse: any,
    isError: boolean
  ): void {
    associatedRequest.body =
      associatedRequest.body == null ? associatedRequest.body : ENCRYPTED_REDACTION;
    associatedRequest.params = associatedRequest.params
      ? ENCRYPTED_REDACTION
      : associatedRequest.params;

    let headers: HttpHeaders | undefined = associatedRequest.headers;
    ENCRYPTED_SERVICE_SENSITIVE_HEADERS.forEach((name) => {
      if (headers?.has(name)) {
        headers = headers.set(name, ENCRYPTED_REDACTION);
      }
    });
    associatedRequest.headers = headers;

    if (!isError) {
      associatedResponse.body = ENCRYPTED_REDACTION;
    }
  }

  isError(response) {
    if (response.error !== undefined) return 'true';
    else return 'false';
  }

  async sendMetricNewRelic2(request, response) {
    switch (this.endPointName) {
      case this.endPointMicroservices[0]:
        this.sendMetricsDoCheckin(request, response, this.endPointName);
        break;

      case this.endPointMicroservices[1]:
        this.sendMetricsBoardingPass(request, response, this.endPointName);
        break;

      default:
        break;
    }
  }

  sendMetricsDoCheckin(request, response, name) {
    let data: any;
    let bookedLeg: any = response.body.bookedLegCollection;
    let passengers: any = JSON.parse(JSON.stringify(response.body.passengers));
    const lightPassenger: any = this.deleteFieldFromJson(
      'travelDoc',
      JSON.parse(JSON.stringify(response.body.passengers))
    );

    for (const passenger of passengers) {
      const { passengerId, checkinStatusBySegment } = passenger;

      for (const segment of checkinStatusBySegment) {
        const { segmentCode, checkinStatus, onStandByList } = segment;

        data = this.getNewRelicParams(
          request,
          response,
          name,
          checkinStatus,
          passengerId,
          null,
          segmentCode,
          onStandByList
        );

        const responseForNewRelic = JSON.parse(JSON.stringify(response));
        this.deleteFieldFromJson('bookedLegCollection', responseForNewRelic);
        this.deleteFieldFromJson('passengers', responseForNewRelic);
        this.deleteFieldFromJson('barcodeImage', lightPassenger);
        this.deleteFieldFromJson('qrCodeImage', lightPassenger);
        this.deleteFieldFromJson('googleWalletPass', lightPassenger);
        this.deleteFieldFromJson('appleWalletPass', lightPassenger);

        data.microResponse = responseForNewRelic;
        data.bookedLegCollection = bookedLeg;
        data.passengers = this.truncate(JSON.stringify(lightPassenger), 4085);
        data.travelDoc = segment?.boardingPass?.travelDoc;

        this._newRelicService.sendMetric(data);
      }
    }

    if (data?.passengers === undefined) {
      data = this.getNewRelicParams(request, response, name, false, null, null, null, null);

      this._newRelicService.sendMetric(data);
    }
  }

  sendMetricsBoardingPass(request, response, name) {
    let data: any;
    let bookedLeg: any = response.body.bookedLegCollection;
    let passengers: any = JSON.parse(JSON.stringify(response.body.passengers));
    const lightPassenger: any = this.deleteFieldFromJson(
      'travelDoc',
      JSON.parse(JSON.stringify(response.body.passengers))
    );

    for (const passenger of passengers) {
      const { checkinStatusBySegment, passengerNameNumber } = passenger;

      for (const segment of checkinStatusBySegment) {
        const { segmentCode } = segment;
        const pectab =
          response?.body?.pectabList.length > 0 ? response?.body?.pectabList[0].pectab : null;

        data = this.getNewRelicParams(
          request,
          response,
          name,
          false,
          passengerNameNumber,
          pectab,
          segmentCode,
          false
        );

        data.bookedLegCollection = bookedLeg;
        data.passengers = this.truncate(JSON.stringify(lightPassenger), 4085);
        data.travelDoc = segment?.boardingPass?.travelDoc;

        const responseForNewRelic = JSON.parse(JSON.stringify(response));
        this.deleteFieldFromJson('bookedLegCollection', responseForNewRelic);
        this.deleteFieldFromJson('passengers', responseForNewRelic);
        this.deleteFieldFromJson('barcodeImage', lightPassenger);
        this.deleteFieldFromJson('qrCodeImage', lightPassenger);
        this.deleteFieldFromJson('googleWalletPass', lightPassenger);
        this.deleteFieldFromJson('appleWalletPass', lightPassenger);

        data.microResponse = responseForNewRelic;

        this._newRelicService.sendMetric(data);
      }
    }

    if (response?.body.pectabList === undefined) {
      data = this.getNewRelicParams(request, response, name, false, null, null, null, null);

      this._newRelicService.sendMetric(data);
    }
  }

  getNewRelicParams(
    request: HttpRequest<unknown>,
    response: HttpResponse<unknown>,
    name: string,
    checkinStatus: boolean,
    paxId: string,
    pectab: string,
    segmentCode: string,
    onStandByList: boolean
  ) {
    const dataNewRelic: NewRelicParmsInt = {
      admin: this._adminService.getDataAdminInt(),
      enviroment: environment.production,
      microRequest: request,
      microResponse: response,
      url: request.url,
      name,
      transactionId: uuidv4(),
      passengerId: paxId,
      isError: this.getIsError(checkinStatus, onStandByList, pectab),
      route: segmentCode,
    };

    return dataNewRelic;
  }

  getIsError(checkinStatus: boolean, onStandByList: boolean, pectab: string): string {
    return checkinStatus || onStandByList || pectab ? 'false' : 'true';
  }

  deleteFieldFromJson(fieldName: string, response: any) {
    if (typeof response === 'object' && response !== null) {
      for (let key in response) {
        if (key === fieldName) {
          delete response[key];
        } else if (typeof response[key] === 'object') {
          this.deleteFieldFromJson(fieldName, response[key]);
        }
      }
    }

    return response;
  }

  truncate(objeto: any, longitudMaxima: number): any {
    if (typeof objeto === 'string') {
      return objeto.length > longitudMaxima ? objeto.substring(0, longitudMaxima) + '...' : objeto;
    }

    return objeto;
  }
}
